import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button as Buttons, Modal, Spacer, Typography } from '@telus-uds/components-web';
import CouponPopup from './couponPopup';
import AppliedCouponsTable from './appliedCouponsTable';
import { useLocaleContext } from '../context/LocaleContext';
import request from '../utils/request';
import { couponTypeBillCredit } from '../utils/constant';
import { validateCouponAgainstUserSelections } from '../utils/couponValidation';

const CouponButton = ({ 
  formFields, 
  orderData, 
  applyCouponPricing, 
  unapplyCouponPricing,
  onChange, 
  readonly = false ,
  appliedCoupons,
  setAppliedCoupons,
  setIsFormTouched,
  isFormValidated,
  setIsFormValidated,
  isCouponButtonVisible,
  currentSite = 1,
  isMultisiteOrder = false
}) => {
  const [isCouponPopupOpen, setIsCouponPopupOpen] = useState(false);
  const [appliedCouponData, setAppliedCouponData] = useState({});
  const [modalData, setModalData] = useState({ open: false, title: '', message: '' });
  const locale = useLocaleContext();
  const isFrench = locale === 'fr-CA';

  useEffect(() => {
    if (orderData && orderData.appliedCoupons) {
      const couponDataMap = {};
      orderData.appliedCoupons.forEach(coupon => {
        if (coupon.couponCode) {
          couponDataMap[coupon.couponCode] = coupon;
        }
      });
      setAppliedCouponData(couponDataMap);
    }
  }, [orderData && orderData.appliedCoupons]);

  const handleCouponApplied = (coupon) => {
    // Extract coupon data using consistent structure
    const couponData = coupon.coupon || coupon;
    const couponCode = (couponData.couponCode || '').trim();
    const couponType = couponData.couponType;
    const description = isFrench ? (couponData.descInstallFr || couponData.descInstallEn || '') : (couponData.descInstallEn || '');
    
    const isBillCredit = couponType.toLowerCase().includes(couponTypeBillCredit);
    const filteredCoupons = (appliedCoupons || []).filter(existingCoupon => {
      const existingType = (existingCoupon.couponType || '').toLowerCase();
      if (isBillCredit) {
        return true;
      } else {
        // Check if existing coupon type contains the same type as the new coupon
        return !existingType.includes(couponType.toLowerCase()) && 
               !couponType.toLowerCase().includes(existingType);
      }
    });

    if (!appliedCouponData[couponCode]) {
      // Apply bill credit pricing if it's a bill credit coupon

      let displayValue = couponCode;
      if (isBillCredit && description) {
        displayValue = `${couponCode} - ${description}`;
      }
       
      setAppliedCouponData(prev => {
        const updated = { ...prev };
        // Only remove old coupon data if not a bill credit
        if (!isBillCredit) {
          Object.keys(updated).forEach(key => {
            const existingCouponType = (updated[key].couponType || '').toLowerCase();
            // Check if the existing coupon type contains the same type as the new coupon
            if (existingCouponType.includes(couponType.toLowerCase()) || 
                couponType.toLowerCase().includes(existingCouponType)) {
              delete updated[key];
            }
          });
        }
        return {
          ...updated,
          [couponCode]: coupon
        };
      });

      if (orderData && orderData.id) {
        const couponDataToSave = {
          couponCode,
          couponType,
          description,
          dollarValue: couponData.dollarValue || 0,
          duration: couponData.duration || '',
          appliedAt: new Date().toISOString(),
          displayValue,
          isApplied: true,
          ...coupon
        };
        const payload = {
          ...orderData,
          appliedCoupons: [...filteredCoupons, couponDataToSave]
        };

        request.put(`/orders/${orderData.id}`, payload)
          .then(() => {
            if (setAppliedCoupons) {
              setAppliedCoupons(payload.appliedCoupons);
              setIsFormTouched(true);
              if(isFormValidated) {
                setIsFormValidated(false);
              }
            }
          })
          .catch(() => {console.log('error saving coupon data')});
      }
    }
    else if(appliedCouponData[couponCode] || (orderData.appliedCoupons && orderData.appliedCoupons.some(appliedCoupon => appliedCoupon.couponCode === couponCode))){
      setModalData({ open: true, title: 'Confirmation', message: isFrench ? 'Ce code promo a déjà été utilisé' : 'This coupon code has already been used' });
    }
  };

  const handleUnapplyCoupon = async (couponCode) => {
    // Find the coupon being toggled
    const couponToToggle = (appliedCoupons || []).find(coupon => coupon.couponCode === couponCode);
    
    if (!couponToToggle) {
      console.error('Coupon not found:', couponCode);
      return;
    }

    // Check if we're applying (going from false to true)
    const isApplying = !couponToToggle.isApplied;
    
    if (isApplying) {
      // Validate the coupon against current user selections before applying
      try {
        // Get the full coupon data from appliedCouponData
        const fullCouponData = appliedCouponData[couponCode];
        
        if (fullCouponData) {
          // Validate the coupon against current form fields and order data
          const validationResult = validateCouponAgainstUserSelections(
            fullCouponData, 
            formFields, 
            orderData, 
            isFrench,
            { currentSite, isMultisiteOrder }
          );
          
          if (!validationResult.isValid) {
            // Log validation errors to console
            console.error('Coupon validation failed for:', couponCode);
            console.error('Validation errors:', validationResult.errors);
            console.error('Validation message:', validationResult.message);
            
            // Show error modal to user
            setModalData({ 
              open: true, 
              title: isFrench ? 'Erreur de Validation' : 'Validation Error', 
              message: validationResult.message 
            });
            return; // Don't apply the coupon
          } else {
            console.log('Coupon validation passed for:', couponCode);
          }
        } else {
          console.warn('Full coupon data not found for validation:', couponCode);
        }
      } catch (error) {
        console.error('Error validating coupon:', couponCode, error);
        setModalData({ 
          open: true, 
          title: isFrench ? 'Erreur de Validation' : 'Validation Error', 
          message: isFrench ? 'Erreur lors de la validation du coupon' : 'Error validating coupon' 
        });
        return;
      }
    }

    // Toggle isApplied status - if false, set to true; if true, set to false
    const updatedAppliedCoupons = (appliedCoupons || []).map(coupon => {
      if (coupon.couponCode === couponCode) {
        return { ...coupon, isApplied: !coupon.isApplied };
      }
      return coupon;
    });

    const payload = {
      ...orderData,
      appliedCoupons: updatedAppliedCoupons
    };

    request.put(`/orders/${orderData.id}`, payload)
      .then(() => {
        if (setAppliedCoupons) {
          setAppliedCoupons(payload.appliedCoupons);
          setIsFormTouched(true);
          if(isFormValidated) {
            setIsFormValidated(false);
          }
        }
      })
      .catch(() => {console.log('error updating coupon isApplied status')});
  };

  const handleDeleteCoupon = (couponCode) => {
    // Delete coupon removes the coupon completely
    setAppliedCouponData(prev => {
      const updated = { ...prev };
      delete updated[couponCode];
      return updated;
    });

    if (!(appliedCoupons.length === 0)) {
      const updatedAppliedCoupons = (appliedCoupons || []).filter(
        coupon => coupon.couponCode !== couponCode
      );

      const payload = {
        ...orderData,
        appliedCoupons: updatedAppliedCoupons
      };

      request.put(`/orders/${orderData.id}`, payload)
        .then(() => {
         if (setAppliedCoupons) {
            setAppliedCoupons(payload.appliedCoupons);
            setIsFormTouched(true);
            if(isFormValidated) {
                setIsFormValidated(false);
              }
            }
        })
        .catch(() => {console.log('error removing coupon data')});
    }
  };

  const couponObjects = appliedCoupons
    // Bill credit applies to site 1 only. On subsequent sites of a multisite
    // order (where the coupon button is hidden) exclude bill credit from the
    // displayed table, while keeping it in the persisted `appliedCoupons`.
    .filter(coupon => isCouponButtonVisible || !((coupon && coupon.couponType ? coupon.couponType : '').toLowerCase().includes('bill credit')))
    .map(coupon => {
    const {couponCode,couponType,dollarValue,duration,isApplied} = coupon || {};
    const description = isFrench ? coupon.descInstallFr : coupon.descInstallEn;
    
    const fullCouponData = appliedCouponData[couponCode];
    if (fullCouponData) {
      return {
        ...fullCouponData,
        isApplied: isApplied !== undefined ? isApplied : true 
      };
    }
    
    return {
      couponCode,
      couponType,
      dollarValue,
      duration,
      description,
      isApplied: isApplied !== undefined ? isApplied : true,
      coupon
    };
  }).filter(coupon => coupon.couponCode);
  return (
    <div style={{ marginBottom: '16px' }}>
      <Modal
        isOpen={modalData.open}
        onClose={() => setModalData({ open: false, title: '', message: '' })}
        variant={{ width: 'xl' }}
      >
        {modalData.title && (
          <>
            <Typography variant={{ size: 'h2' }}>{modalData.title}</Typography>
            <Spacer space={1} />
          </>
        )}
        <Typography variant={{ size: 'body' }}>{modalData.message}</Typography>
      </Modal>
      {isCouponButtonVisible && <Buttons 
        variant={{ priority: 'high' }} 
        onPress={() => setIsCouponPopupOpen(true)}
        disabled={readonly}
      >
        {isFrench ? 'Ajouter un Code de Coupon' : 'Add Coupon Code'}
      </Buttons>}
      
      <AppliedCouponsTable 
        couponObjects={couponObjects}
        onUnapplyCoupon={handleUnapplyCoupon}
        onDeleteCoupon={handleDeleteCoupon}
        isCouponButtonVisible={isCouponButtonVisible}
      />
      
      {orderData && (
        <CouponPopup
          isOpen={isCouponPopupOpen}
          onClose={() => setIsCouponPopupOpen(false)}
          onCouponApplied={handleCouponApplied}
          formFields={formFields}
          orderData={orderData}
          currentSite={currentSite}
          isMultisiteOrder={isMultisiteOrder}
        />
      )}
    </div>
  );
};

CouponButton.propTypes = {
  formFields: PropTypes.shape({}).isRequired,
  orderData: PropTypes.shape({}).isRequired,
  applyCouponPricing: PropTypes.func.isRequired,
  unapplyCouponPricing: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  readonly: PropTypes.bool,
  applyBillCreditCoupon: PropTypes.func,
  unapplyBillCreditCoupon: PropTypes.func,
  currentSite: PropTypes.number,
  isMultisiteOrder: PropTypes.bool,
};

export default CouponButton;